import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

import {eatClubClient} from "@globals/config/apiClient";
import {ResponseError} from "@globals/types/utilityTypes";

import {SamlConfiguration, SamlConfigurationResource} from "./interfaces";

const samlUrl = "/customers/company-saml-configuration/";

function fetchSamlConfiguration(): Promise<SamlConfiguration> {
  return eatClubClient
    .get(samlUrl)
    .then((response) => ({
      saml_cert: "",
      saml_default_pg_id: "",
      saml_entity_id: "",
      saml_url: "",
      ...response.data,
    }))
    .catch(({response}) => {
      const error = {
        status: response.status,
        message: response.data?.message,
      };
      throw error;
    });
}

function saveSamlConfiguration(
  data: SamlConfiguration
): Promise<SamlConfiguration> {
  return eatClubClient
    .put(samlUrl, data)
    .then(() => data)
    .catch(({response}) => {
      const error = {
        status: response.status,
        message: response.data?.message,
      };
      throw error;
    });
}

export default function useSamlConfiguration(): SamlConfigurationResource {
  const queryClient = useQueryClient();

  const {data, isPending, isError, error} = useQuery<
    SamlConfiguration,
    ResponseError
  >({queryKey: ["saml-configuration"], queryFn: fetchSamlConfiguration});
  const mutation = useMutation({
    mutationFn: saveSamlConfiguration,
    onSuccess: (data) => {
      queryClient.setQueryData(["saml-configuration"], data);
    },
  });

  return {
    data,
    save: mutation.mutate,
    isLoading: isPending || mutation.isPending,
    isError: isError || mutation.isError,
    isSaved: mutation.isSuccess,
    error: error || (mutation.error as ResponseError),
  };
}
